import { useEffect, useState, useContext, useCallback } from 'react';
import { Route, Redirect } from 'react-router-dom';
import { AuthContext } from 'context/auth-context';
import { LoaderPage } from 'pages/Loader.page';
import { Banks, Owners, KYB as APIKYB, Auth } from 'lib/api';
import { PAYSTAND_SUPPORT_URL, USER_ROLES } from 'helpers/constants';
import FrozenAccountAlert from 'components/shared/FrozenAccountAlert';
import useAuth from 'Hooks/useAuth';

export const PrivateRoute = ({ component: Component, ...rest }) => {
    const [isLoading, setIsLoading] = useState(true);
    const { userInfo, businessInfo, setBusinessInfo, loginUser, setHasAccount, setUserInfo } = useContext(AuthContext);
    const decodeState = useAuth();

    const fetchUserInfo = useCallback(async () => {
        try {
            const currentUserInfo = await Auth.getUserInfo();
            loginUser({
                fullName: `${currentUserInfo.first_name} ${currentUserInfo.last_name}`,
                firstName: currentUserInfo.first_name,
                lastName: currentUserInfo.last_name,
                email: currentUserInfo.email
            });
            setUserInfo({
                ...currentUserInfo,
                loaded: true
            });
            setHasAccount(currentUserInfo.account);
        } catch (error) {
            console.error(error);
            // eslint-disable-next-line no-restricted-globals
            location.pathname = '/login';
        }
    }, []);
    useEffect(() => {
        fetchUserInfo();
    }, [fetchUserInfo]);

    useEffect(() => {
        // User info not loaded yet.
        if (!userInfo.loaded) return;
        // User is not an admin
        /*         if (userInfo?.role?.id === USER_ROLES.CARD_HOLDER) {
            setBusinessInfo(cBusiness => ({
                ...cBusiness,
                loaded: true
            }));
            return;
        } */
        // Loaded but KYB has not been started.
        if (!userInfo.kyb) {
            setBusinessInfo(cBusiness => ({
                ...cBusiness,
                loaded: true
            }));
            return;
        }

        if (userInfo?.role?.id === USER_ROLES.SUPERADMIN) {
            setBusinessInfo(cBusiness => ({
                ...cBusiness,
                loaded: true
            }));
            return;
        }

        // KYB started
        Promise.all([
            APIKYB.getBusiness().catch(() => {
                return {};
            }),
            Owners.getOwners().catch(() => {
                return {
                    rows: {}
                };
            }),
            Banks.getTheBanks().catch(() => {
                return {
                    rows: {}
                };
            })
        ])
            .then(values => {
                const [businessResponse, ownerResponse, bankResponse] = values;
                setBusinessInfo(cBusiness => ({
                    ...cBusiness,
                    ...businessResponse,
                    owners: ownerResponse.rows,
                    banks: bankResponse.rows,
                    loaded: true
                }));
            })
            .catch(error => {
                setBusinessInfo(cBusiness => ({
                    ...cBusiness,
                    owners: [],
                    banks: [],
                    loaded: true
                }));
            });
    }, [userInfo, setBusinessInfo]);

    useEffect(() => {
        if (userInfo.loaded && businessInfo.loaded) {
            setIsLoading(false);
        }
    }, [userInfo.loaded, businessInfo.loaded]);

    return (
        <>
            {isLoading ? (
                <LoaderPage />
            ) : (
                <>
                    {!!businessInfo?.is_frozen && (
                        <FrozenAccountAlert>
                            Your account has been suspended temporarily. Please contact support{' '}
                            <a href={PAYSTAND_SUPPORT_URL} target="_blank" rel="noreferrer">
                                here
                            </a>{' '}
                            if you have any questions.
                        </FrozenAccountAlert>
                    )}
                    <Route
                        {...rest}
                        render={props => {
                            if (decodeState?.id) {
                                return <Component {...props} />;
                            }
                            return <Redirect to="/login" />;
                        }}
                    />
                </>
            )}
        </>
    );
};
